import React, { useEffect, useMemo } from 'react';
import { AppContainer } from '../../../../components/pixie/AppContainer';
import { ExternalGraphQLClientProvider } from './ExternalGraphQLClientProvider';
import { StylingWithDefaults } from '../../../../types/ClientState';
import { TFont, TLayout, TPalette } from '../../../../../generated/gql/graphql';
import { defaultLayout, useClientStore } from '../../../../hooks/ClientState';
import { AppClientInPopover } from '../Web/AppClient';
import { useShallow } from 'zustand/react/shallow';
import { Theme } from '@mui/material';
import { graphql } from '../../../../../generated/gql/gql';
import { useEditorStore } from '../../../../hooks/EditorState';
import { EmbeddedAppContext } from '../../../../contexts/EmbeddedAppContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PluginTypesLoader } from './PluginTypesLoader';


export interface EmbeddedChatAppProps {
  authToken?: string;
  useLocalhost?: boolean;
  palette?: TPalette;
  font?: TFont;
  layout?: TLayout;
  onStylingLoaded?: (styling: StylingWithDefaults) => void;
  theme?: Partial<Theme>;
  isChromeExtension?: boolean;
  // tabId of the current tab hosting the embedded app
  tabId?: number;
  // tabId of the parent app that opened this app
  parentTabId?: number;
  fullWidth?: boolean;
  replayEffectsOnConnect?: boolean;
}

export function EmbeddedChatApp(props: EmbeddedChatAppProps): React.ReactElement {
  const flowId = useClientStore(state => state.flowId);
  const debug = useEditorStore(state => state.graph.nodes.length > 0);

  const embeddedAppContext = useMemo(() => ({
    isEmbedded: true,
    isChromeExtension: props.isChromeExtension,
    useLocalhost: props.useLocalhost,
    parentTabId: props.parentTabId,
    tabId: props.tabId,
  }), [props.isChromeExtension, props.useLocalhost, props.parentTabId, props.tabId]);

  return <GoogleOAuthProvider clientId='704936193834-0alolkp97r5g459b0nd5bdmt7484s6bk.apps.googleusercontent.com'>
    <ExternalGraphQLClientProvider useLocalhost={props.useLocalhost} authToken={props.authToken}>
      <AppContainer>
        <EmbeddedAppContext.Provider value={embeddedAppContext}>
          <PluginTypesLoader shouldLoad={debug}>
            <AppClientInPopover
              flowId={flowId}
              {...props}
              debug={debug}
              layout={debug
                ? {
                  ...defaultLayout,
                  dialogWidth: '100vw',
                  dialogHeight: '100vh',
                }
                : props.layout
              }
              fullWidth={props.fullWidth}
            />
          </PluginTypesLoader>
        </EmbeddedAppContext.Provider>
      </AppContainer>
    </ExternalGraphQLClientProvider>
  </GoogleOAuthProvider>;
}

// TODO option to use localStorage instead of sessionStorage
export function EmbeddedChatAppWithMemory(props: EmbeddedChatAppProps): React.ReactElement {

  const flowId = useClientStore(state => state.flowId);
  const setClientId = useClientStore(state => state.setClientId);

  useEffect(() => {
    const cid = sessionStorage.getItem(flowId);
    if (cid) {
      setClientId(cid);
    }
  }, [flowId]);

  useEffect(() => {
    const unsub = useClientStore.subscribe(
      state => [state.clientId, state.flowId],
      ([clientId, flowId]) => {
        if (flowId && clientId) {
          sessionStorage.setItem(flowId, clientId);
        }
      },
    );
    return unsub;
  }, []);

  return <EmbeddedChatApp {...props} />;
}
